import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { DataTable } from '@/components/ui/table/data-table';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import { useFilteredBySearch } from '@/modules/collections/hooks/useFilteredBySearch';
import { getLastVisitedCategoryForCollection } from '@/modules/collections/hooks/useLastVisitedCategory';
import DeleteCollectionModal from '@/modules/collections/modals/DeleteCollectionModal';
import { selectAccountSlug } from '@/redux/session/selectors';

import { columns } from './columns';

interface CollectionsTableProps {
  activeRowId?: string;
}

export const CollectionsTable = ({ activeRowId }: CollectionsTableProps) => {
  const { t } = useTranslation();
  const { collectionId } = useParams();
  const { collections, listStatus } = useCollections(collectionId);
  const { brains } = useBrains();
  const slug = useSelector(selectAccountSlug);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCollections = useFilteredBySearch(collections ?? [], searchTerm);

  const tableColumns = useMemo(() => columns({ brains }), [brains]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex items-center justify-between gap-2 pl-1">
        <SearchBar
          placeholder={t('collections.search_collections')}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      <DataTable.Root
        columns={tableColumns}
        data={filteredCollections}
        rowCount={filteredCollections.length}
        isLoading={listStatus === 'pending'}
        resourceName="collection"
        emptyStateMessage={t('collections.no_collections_found')}
        getRowId={(row) => row.collection_id}
        activeRowId={activeRowId}
        enableRowHover
        getRowHref={(row) =>
          `/${slug}/knowledge-bases/${row.collection_id}/category/${getLastVisitedCategoryForCollection(row.collection_id)}`
        }
        autoResetPageIndex
        fill
        className="min-h-0 flex-1"
      >
        <DataTable.Content />
        <DataTable.Pagination />
      </DataTable.Root>

      <DeleteCollectionModal />
    </div>
  );
};
